/**
 * FinanzasLayout — Sidebar secundaria (desktop) + Tabs scrolleables (mobile).
 *
 * Reemplaza la antigua "Agencias → Comisiones" promoviendo el módulo a su
 * propio espacio. El alcance real siempre fue financiero: cobros, vouchers,
 * deudas, comisiones, liquidaciones, cruces, ventas directas. El nombre
 * "Comisiones" era un legado de cuando arrancó solo con ese dashboard.
 *
 * Agrupación en 3 secciones para que cada item viva donde semánticamente
 * pertenece:
 *
 *   COBROS — lo que entra: cartera de turistas, vouchers, ventas directas.
 *   AGENCIAS — el ciclo con revendedores: deuda, comisiones, liquidaciones.
 *   CONCILIACIÓN — cruces y compensaciones entre saldos.
 *
 * Mismo patrón visual que AgenciasLayout / CatalogoLayout / BookingLayout.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Tabs, Tab,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import PaymentsIcon from '@mui/icons-material/Payments';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// Secciones del sidebar — agrupadas por dominio.
// Color por sección para que el ojo identifique en qué bloque está.
// Helper: rol del usuario logueado — controla qué items son visibles.
function _getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}

const SECCIONES = [
  {
    grupo: 'Visión general',
    color: '#0d9488',
    items: [
      { path: 'foto-dia',        label: 'Foto del día',     icon: <PhotoCameraIcon /> },
      { path: 'no-shows',        label: 'No-shows',         icon: <NoteAltIcon /> },
      { path: 'pagos-por-reserva', label: 'Pagos por reserva', icon: <PaymentsIcon /> },
      // Solo admin/superadmin — es una vista de control operativo sobre reservas
      // con voucher faltante (información sensible que no debe ver un comercial).
      { path: 'pendientes-subsanar', label: 'Pendientes por subsanar', icon: <NoteAltIcon />, allowedRoles: ['admin','superadmin'] },
    ],
  },
  {
    grupo: 'Cobros',
    color: '#0d9488',
    items: [
      { path: 'vouchers',        label: 'Vouchers',         icon: <PaymentIcon /> },
    ],
  },
  {
    grupo: 'Agencias',
    color: '#be185d',
    items: [
      { path: 'estado-cuenta', label: 'Estado de cuenta', icon: <AssignmentIndIcon /> },
      { path: 'deuda',         label: 'Deuda',            icon: <AccountBalanceIcon /> },
      { path: 'cxc',           label: 'CxC (cupo)',       icon: <AccountBalanceWalletIcon /> },
      { path: 'aging',         label: 'Aging cartera',    icon: <StackedBarChartIcon /> },
      { path: 'comisiones',    label: 'Comisiones por pagar', icon: <TrendingUpIcon /> },
      { path: 'cronograma',    label: 'Proyección de pagos',  icon: <CalendarMonthIcon /> },
      { path: 'liquidaciones', label: 'Comisiones pagadas',   icon: <HistoryIcon /> },
    ],
  },
  {
    grupo: 'Conciliación',
    color: '#7c3aed',
    items: [
      { path: 'cruce', label: 'Cruce', icon: <CompareArrowsIcon /> },
    ],
  },
];

// El rol COMERCIAL solo ve estas tres pantallas de Finanzas, y en consulta.
// Es una lista BLANCA a propósito: si mañana se agrega una pantalla nueva,
// nace invisible para el comercial y hay que habilitarla explícitamente. Con
// una lista negra pasaría lo contrario — se filtraría sola y nadie lo notaría.
export const FINANZAS_COMERCIAL = ['no-shows', 'cxc', 'comisiones'];

// Filtrar items por rol antes de flatten — items con allowedRoles definido
// solo aparecen si el rol del usuario está en la lista. Sin allowedRoles = público.
function _filtrarPorRol(secciones, role) {
  const esComercial = (role || '').toLowerCase() === 'comercial';
  return secciones.map(s => ({
    ...s,
    items: s.items.filter(i => {
      if (esComercial) return FINANZAS_COMERCIAL.includes(i.path);
      return !i.allowedRoles || i.allowedRoles.includes(role);
    }),
  })).filter(s => s.items.length > 0);
}

export default function FinanzasLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Filtrar secciones e items según el rol del usuario logueado. Recalculado
  // en cada render por si el usuario cambia de sesión sin recargar.
  const rolUsuario = _getUserRole();
  const secciones = _filtrarPorRol(SECCIONES, rolUsuario);
  const todosLosItems = secciones.flatMap(s =>
    s.items.map(i => ({ ...i, grupoColor: s.color, grupo: s.grupo }))
  );

  // Redirigir a "cartera" si entran a /finanzas sin sub-ruta
  useEffect(() => {
    if (location.pathname === '/finanzas' || location.pathname === '/finanzas/') {
      navigate(rolUsuario === 'comercial' ? '/finanzas/cxc' : '/finanzas/foto-dia',
               { replace: true });
    }
  }, [location.pathname, navigate, rolUsuario]);

  // Un comercial que escriba a mano /finanzas/vouchers no debe entrar solo
  // porque el menú no se lo muestre. Ocultar no es proteger: aquí se saca de
  // la ruta, y el backend además rechaza cualquier escritura suya.
  useEffect(() => {
    if ((rolUsuario || '').toLowerCase() !== 'comercial') return;
    const sub = location.pathname.replace(/^\/finanzas\/?/, '').split('/')[0];
    if (sub && !FINANZAS_COMERCIAL.includes(sub)) {
      navigate('/finanzas/cxc', { replace: true });
    }
  }, [location.pathname, navigate, rolUsuario]);

  const activeIdx = Math.max(0, todosLosItems.findIndex(
    s => location.pathname.includes(`/finanzas/${s.path}`)
  ));
  const itemActivo = todosLosItems[activeIdx];

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario (desktop) */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Finanzas
          </Typography>
        </Box>

        <List sx={{ px: 1, py: 0, flex: 1, overflowY: 'auto' }} dense>
          {secciones.map(seccion => (
            <Box key={seccion.grupo}>
              {/* Header del grupo — sutil, no consume mucha altura */}
              <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                <Typography sx={{
                  fontSize: 9.5, fontWeight: 800, letterSpacing: 0.8,
                  textTransform: 'uppercase', color: seccion.color,
                }}>
                  {seccion.grupo}
                </Typography>
              </Box>
              {seccion.items.map(item => {
                const isActive = location.pathname.includes(`/finanzas/${item.path}`);
                return (
                  <ListItemButton key={item.path}
                    onClick={() => navigate(`/finanzas/${item.path}`)}
                    sx={{
                      borderRadius: 2, mb: 0.25, py: 0.75, pl: 1.5,
                      bgcolor: isActive ? `${seccion.color}15` : 'transparent',
                      borderLeft: isActive ? `3px solid ${seccion.color}` : '3px solid transparent',
                      '&:hover': { bgcolor: isActive ? `${seccion.color}25` : '#f1f5f9' },
                    }}>
                    <ListItemIcon sx={{ minWidth: 32, color: isActive ? seccion.color : '#94a3b8' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 13, fontWeight: isActive ? 700 : 500,
                        color: isActive ? '#0f172a' : '#475569',
                      }} />
                  </ListItemButton>
                );
              })}
            </Box>
          ))}
        </List>

        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Cuentas por cobrar, comisiones a agencias, liquidaciones y conciliación.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Tabs móviles — planos (sin agrupación, scroll horizontal) */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: 0, zIndex: 5,
        }}>
          <Tabs
            value={activeIdx}
            onChange={(_, idx) => navigate(`/finanzas/${todosLosItems[idx].path}`)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 700, fontSize: 12 },
              '& .MuiTabs-indicator': {
                backgroundColor: itemActivo?.grupoColor || '#0d9488',
                height: 3, borderRadius: 2,
              },
            }}
          >
            {todosLosItems.map(item => (
              <Tab key={item.path} icon={item.icon} iconPosition="start" label={item.label} />
            ))}
          </Tabs>
        </Box>

        <Outlet />
      </Box>
    </Box>
  );
}
